import type {
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { createContext, useCallback } from 'react';
import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  GetReceiptBatchPaymentPhoenixResult,
  IPaymentPhoenixApi,
  MangopayPaymentResolution,
  MangopayReceiptBatchEntry,
  MangopayReceiptBatchEntryCancellation,
  MangopayReceiptBatchPayment,
} from './api';
import { MangopayPaymentStatus, PaymentPhoenixApi } from './api';

const ApiContext = createContext<IPaymentPhoenixApi | undefined>(undefined);
const { useApi: usePaymentPhoenixApi, ApiProvider: PaymentPhoenixApiProvider } =
  makeApiProvider({
    name: 'PaymentPhoenix',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) => {
      return new PaymentPhoenixApi(tenantId, auth, apiUrl);
    },
  });

export {
  usePaymentPhoenixApi,
  PaymentPhoenixApiProvider,
  ApiContext as PaymentPhoenixApiContext,
  GetReceiptBatchPaymentPhoenixResult,
  MangopayReceiptBatchEntry,
  MangopayReceiptBatchEntryCancellation,
  MangopayPaymentResolution,
  MangopayPaymentStatus,
  MangopayReceiptBatchPayment,
};

export const getReceiptBatchPaymentPhoenix = (
  receiptBatchId: string | undefined
): QueryKey => ['GET_RECEIPT_BATCH_PAYMENT_PHOENIX', receiptBatchId];

export const useGetReceiptBatchPaymentPhoenix = ({
  receiptBatchId,
  options,
}: {
  receiptBatchId: string | undefined;
  options?: Omit<
    UseQueryOptions<GetReceiptBatchPaymentPhoenixResult, unknown>,
    'queryFn' | 'queryKey'
  >;
}): UseQueryResult<GetReceiptBatchPaymentPhoenixResult, unknown> => {
  const select = useCallback(
    (data: GetReceiptBatchPaymentPhoenixResult) => ({
      ...data,
      data: {
        ...data.data,
        receipt_batch_payment: {
          ...data.data.receipt_batch_payment,
          receipt_batch_entries:
            data.data.receipt_batch_payment.receipt_batch_entries.map(
              (entry) => ({
                ...entry,
                embargo_date: entry.embargo_date
                  ? new Date(entry.embargo_date)
                  : undefined,
                cancellation: entry.cancellation
                  ? {
                      ...entry.cancellation,
                      date: new Date(entry.cancellation.date),
                    }
                  : undefined,
                transfer_datetime: entry.transfer_datetime
                  ? new Date(entry.transfer_datetime)
                  : undefined,
                resolution: entry.resolution
                  ? {
                      ...entry.resolution,
                      resolution_date: new Date(
                        entry.resolution.resolution_date
                      ),
                    }
                  : undefined,
              })
            ),
        },
      },
    }),
    []
  );

  return useApiQuery(
    usePaymentPhoenixApi,
    getReceiptBatchPaymentPhoenix(receiptBatchId),
    (api) => {
      if (!receiptBatchId) {
        throw new Error('receiptBatchId is required to get payment');
      }
      return api.getReceiptBatchPaymentPhoenix({
        params: {
          receiptBatchId,
        },
      });
    },
    {
      ...options,
      enabled: options?.enabled ?? !!receiptBatchId,
      select,
    }
  );
};
